import { useEffect } from 'react';
import CleanupLink from './CleanupLink';
import { getUser } from '../services/authService';

// ─────────────────────────────────────────────────────────────────
// THIRD-PASS FIX — <picture> double-decode hang
//
// Root cause: <picture><source srcSet="x.webp"><img src="x.jpg">
// fires TWO decode pipelines per card — one for the WebP <source>
// and one kept alive for the <img> fallback. 32 cards = 64 decode
// ops hitting simultaneously on mount = hang.
//
// FIX: Drop <picture> entirely. <img src> points directly at .webp.
// One element · one fetch · one decode per card.
// Extensions are baked into the path arrays (no runtime string concat).
//
// Previous fixes retained:
//   · overflow:hidden removed from cards → clip-path:inset on img
//   · will-change on each .hro-track, not the parent panel
//   · contain:layout style paint on .hro-right
//   · mask-image replaced with gradient overlay div
//   · gap replaced by margin-bottom on cards
//   · isolation:isolate removed (contain:paint already does it)
//   · CSS injected once via useEffect, not inline JSX
// ─────────────────────────────────────────────────────────────────

const col1Images = [
  '/images/portfolio/logo1.webp',
  '/images/portfolio/web1.webp',
  '/images/portfolio/logo4.webp',
  '/images/portfolio/web4.webp',
];
const col2Images = [
  '/images/portfolio/logo2.webp',
  '/images/portfolio/web2.webp',
  '/images/portfolio/logo5.webp',
  '/images/portfolio/web5.webp',
];
const col3Images = [
  '/images/portfolio/logo3.webp',
  '/images/portfolio/web3.webp',
  '/images/portfolio/logo6.webp',
  '/images/portfolio/web6.webp',
];
const ghostImages = [
  '/images/portfolio/logo1.webp',
  '/images/portfolio/web3.webp',
  '/images/portfolio/logo5.webp',
  '/images/portfolio/web6.webp',
];

const s1 = [...col1Images, ...col1Images];
const s2 = [...col2Images, ...col2Images];
const s3 = [...col3Images, ...col3Images];
const sg = [...ghostImages, ...ghostImages];

// ─── Static CSS — defined outside component, injected once ───────
const STYLE_ID = 'hro-sec-styles';

const CSS = `
  .hro-sec {
    position: relative;
    min-height: 100vh;
    background: #000000;
    display: flex;
    align-items: center;
    overflow: hidden;
    content-visibility: auto;
    contain-intrinsic-size: auto 100vh;
  }
  .hro-sec::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      135deg,
      rgba(0,0,0,0.02) 0px,
      rgba(0,0,0,0.02) 1px,
      transparent 1px,
      transparent 12px
    );
    pointer-events: none;
    z-index: 1;
    /* Promote to own layer so it doesn't dirty the parent paint surface */
    will-change: transform;
    transform: translateZ(0);
  }
  .hro-glow {
    position: absolute;
    left: -120px;
    top: 50%;
    transform: translateY(-50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(0,0,0,0.13) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
  }
  .hro-inner {
    position: relative;
    z-index: 2;
    width: 100%;
    display: flex;
    align-items: stretch;
    min-height: 100vh;
  }
  .hro-left {
    flex: 0 0 46%;
    max-width: 46%;
    padding: 80px 50px 80px 2vw;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .hro-right {
    flex: 0 0 54%;
    max-width: 54%;
    height: 100vh;
    position: relative;
    overflow: hidden;
    display: flex;
    gap: 12px;
    padding: 0 6px 0 0;
    /* isolation:isolate removed — contain:paint already creates
       a stacking context, no need to do it twice */
    contain: layout style paint;
  }
  .hro-right::before,
  .hro-right::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 160px;
    z-index: 10;
    pointer-events: none;
  }
  .hro-right::before {
    top: 0;
    background: linear-gradient(to bottom, #000000 0%, transparent 100%);
  }
  .hro-right::after {
    bottom: 0;
    background: linear-gradient(to top, #000000 0%, transparent 100%);
  }
  .hro-col-ghost {
    flex: 1;
    overflow: hidden;
    height: 100%;
    z-index: 0;
    opacity: 0.28;
    position: relative;
    /* mask-image removed — replaced by .hro-col-ghost-fade overlay below.
       CSS mask samples every pixel on GPU each frame; a gradient overlay
       is a single composited rectangle quad — far cheaper. */
  }
  .hro-col-ghost-fade {
    position: absolute;
    inset: 0;
    /* Left-to-right fade replacing the mask */
    background: linear-gradient(to right, #000000 0%, transparent 100%);
    z-index: 2;
    pointer-events: none;
  }
  .hro-col {
    flex: 1;
    overflow: hidden;
    height: 100%;
    z-index: 1;
  }
  .hro-track {
    display: flex;
    flex-direction: column;
    /* gap replaced by margin-bottom on .hro-card — avoids flex
       re-layout pass on the track element itself */
    gap: 0;
    will-change: transform;
    transform: translateZ(0);
    backface-visibility: hidden;
  }
  @keyframes hro-up {
    from { transform: translateZ(0) translateY(0); }
    to   { transform: translateZ(0) translateY(-50%); }
  }
  @keyframes hro-down {
    from { transform: translateZ(0) translateY(-50%); }
    to   { transform: translateZ(0) translateY(0); }
  }
  .hro-track-up   { animation: hro-up   120s linear infinite; }
  .hro-track-down { animation: hro-down 120s linear infinite; }
  .hro-card {
    width: 100%;
    aspect-ratio: 2 / 3;
    /* border-radius kept but overflow:hidden REMOVED.
       overflow:hidden on an animated child creates a new stacking context
       per card — up to 32 extra GPU layers. Instead we clip via
       clip-path on the img itself (see below). */
    border-radius: 12px;
    flex-shrink: 0;
    background: #001a26;
    margin-bottom: 12px;
    /* NO transition here — hover transitions inside a compositing layer
       trigger re-compositing on every pointer-move frame */
  }
  .hro-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    pointer-events: none;
    image-rendering: auto;
    /* clip-path does the rounding instead of overflow:hidden on the parent.
       The browser clips at the rasterization step rather than promoting
       a new compositor layer per card. */
    clip-path: inset(0 round 12px);
  }
  .hro-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #00a8cc;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 22px;
  }
  .hro-eyebrow::before {
    content: '';
    display: block;
    width: 32px;
    height: 2px;
    background: #00a8cc;
    border-radius: 2px;
  }
  .hro-heading {
    font-size: clamp(2rem, 3.6vw, 3.1rem);
    font-weight: 800;
    color: #fff;
    line-height: 1.18;
    margin-bottom: 22px;
    letter-spacing: -0.8px;
  }
  .hro-heading em { font-style: normal; color: #00a8cc; }
  .hro-sub {
    color: rgba(255,255,255,0.70);
    font-size: 1.05rem;
    line-height: 1.72;
    max-width: 460px;
    margin-bottom: 36px;
  }
  .hro-actions {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
  @media (max-width: 991px) {
    .hro-inner  { flex-direction: column; }
    .hro-left   { flex: 0 0 100%; max-width: 100%; padding: 100px 24px 40px; }
    .hro-right  { flex: 0 0 100%; max-width: 100%; max-height: 340px; padding: 0 16px 32px; }
    .hro-col-ghost { display: none; }
  }
  @media (max-width: 575px) {
    .hro-heading { font-size: 1.8rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .hro-track-up,
    .hro-track-down { animation: none; }
  }
`;

// ─── Image helper ────────────────────────────────────────────────
// Direct <img> pointing straight at .webp — no <picture> wrapper.
// <picture> with a <source> creates TWO decode attempts per image:
// one for the <source> WebP and one kept alive for the <img> fallback.
// With 32 cards that's 64 simultaneous network+decode ops on mount.
// Extension is baked into the path constants above — no runtime concat.
const Img = ({ src }) => (
  <img
    src={src}
    alt=""
    loading="lazy"
    fetchPriority="low"
    decoding="async"
  />
);

const BannerSection = () => {
  const user = getUser();

  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const tag = document.createElement('style');
    tag.id = STYLE_ID;
    tag.textContent = CSS;
    document.head.appendChild(tag);
    return () => {
      const existing = document.getElementById(STYLE_ID);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <section className="hro-sec">
      <div className="hro-glow" />
      <div className="hro-inner">

        <div className="hro-left">
          <span className="hro-eyebrow">Grand Indigo Embroidery</span>
          <h1 className="hro-heading">
            Professional<br />
            <em>Embroidery Digitizing</em><br />
            &amp; Vector Services
          </h1>
          <p className="hro-sub">
            From adaption to expansion, we are your partners throughout
            the digital growth cycle delivering stitch-perfect files,
            stunning logos, and custom patches that last.
          </p>
          <div className="hro-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.dispatchEvent(new Event('open-home-contact-modal'))}
            >
              Get A Free Quote
            </button>
            <CleanupLink className="btn btn-primary" to={user ? '/user-dashboard' : '/login-signup'}>
              {user ? 'Visit Dashboard' : 'Login / Signup'}
            </CleanupLink>
          </div>
        </div>

        <div className="hro-right">

          <div className="hro-col-ghost">
            <div className="hro-col-ghost-fade" />
            <div className="hro-track hro-track-down">
              {sg.map((src, i) => (
                <div key={i} className="hro-card"><Img src={src} /></div>
              ))}
            </div>
          </div>

          <div className="hro-col">
            <div className="hro-track hro-track-down">
              {s1.map((src, i) => (
                <div key={i} className="hro-card"><Img src={src} /></div>
              ))}
            </div>
          </div>

          <div className="hro-col">
            <div className="hro-track hro-track-up">
              {s2.map((src, i) => (
                <div key={i} className="hro-card"><Img src={src} /></div>
              ))}
            </div>
          </div>

          <div className="hro-col">
            <div className="hro-track hro-track-down">
              {s3.map((src, i) => (
                <div key={i} className="hro-card"><Img src={src} /></div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BannerSection;